import * as React from 'react';

/** A business's brand as the API publishes it (Receipt-Web-Server management/brand.py). */
export type BrandColors = {
  background: string; surface: string; text: string; primary: string; accent: string;
  on_primary: string; on_accent: string; muted: string; border: string;
};
export type BrandFont = { id: string; label: string; family: string; stretch: string | null };
export type PublicBrand = {
  colors: BrandColors;
  fonts: { heading: BrandFont; body: BrandFont };
  fonts_css: string;
  tagline: string;
  /** What the business calls its products on its pages (Menú by default). */
  catalog_label?: string;
  /** The business's privacy notice, linked from pages that show customers' data. */
  privacy_url?: string;
  /** The business's own website where customers order: the customer's card sends them there with their card. */
  order_url?: string;
  logo: string | null;
  cover: string | null;
};

export const fontStack = (font: BrandFont) => `'${font.family}', Inter, system-ui, -apple-system, sans-serif`;

const channels = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));

const luminance = (hex: string) => {
  const [r, g, b] = channels(hex).map(c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrast = (a: string, b: string) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** Same rules as the server: the text color (dark or white) that reads best on a background. */
export const readableOn = (background: string, dark = '#14252C', light = '#FFFFFF') =>
  contrast(background, light) >= contrast(background, dark) ? light : dark;

export const mix = (a: string, b: string, weight: number) => {
  const ca = channels(a);
  const cb = channels(b);
  return '#' + ca.map((x, i) => Math.round(x * (1 - weight) + cb[i] * weight).toString(16).padStart(2, '0')).join('').toUpperCase();
};

/** Derived colors for a draft brand in the dashboard preview (the server computes them for saved brands). */
export const completeColors = (c: Pick<BrandColors, 'background' | 'surface' | 'text' | 'primary' | 'accent'>): BrandColors => ({
  ...c,
  on_primary: readableOn(c.primary),
  on_accent: readableOn(c.accent),
  muted: mix(c.text, c.surface, 0.4),
  border: mix(c.text, c.surface, 0.86),
});

/** Loads a Google Fonts stylesheet once; other addresses are ignored. */
export const useFontsStylesheet = (href?: string) => {
  React.useEffect(() => {
    if (!href || !href.startsWith('https://fonts.googleapis.com/')) return;
    if ([...document.querySelectorAll('link[data-brand-fonts]')].some(l => (l as HTMLLinkElement).href === href)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.brandFonts = '1';
    document.head.appendChild(link);
  }, [href]);
};

const INTER: BrandFont = { id: 'inter', label: 'Inter', family: 'Inter', stretch: null };

/** TRUE's look, for a business without a brand (and servers that don't publish one). */
export const DEFAULT_BRAND: PublicBrand = {
  colors: completeColors({ background: '#F2F5F7', surface: '#FFFFFF', text: '#14252C', primary: '#14252C', accent: '#14252C' }),
  fonts: { heading: INTER, body: INTER },
  fonts_css: '',
  tagline: '',
  logo: null,
  cover: null,
};
